import {
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  CONDITION_LABEL,
  PERMISSIONS,
  VehicleCondition,
  WaybillStatus,
  needsConditionAct,
} from '@tms/shared';

import { EntityAuditLog } from '@/components/EntityAuditLog';
import { EntityId } from '@/components/EntityId';
import { api } from '@/api/client';
import { useApiMutation, useDictionaries, useDownload } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { MedicalClearanceCard } from './MedicalClearanceCard';
import { TechnicalClearanceCard } from './TechnicalClearanceCard';
import { WaybillFormModal } from './WaybillFormModal';
import {
  WAYBILL_STATUS_COLOR,
  WAYBILL_STATUS_LABEL,
  deviationColor,
  fmt,
} from '@/lib/labels';

interface Props {
  waybillId: number | null;
  onClose: () => void;
}

interface WaybillDetail {
  id: number;
  number: string;
  status: string;
  type: string;
  validFrom: string;
  validTo: string;
  departedAt: string | null;
  odometerStart: string | null;
  odometerEnd: string | null;
  engineHoursStart: string | null;
  engineHoursEnd: string | null;
  distanceKm: string | null;
  engineHours: string | null;
  fuelOpening: string;
  fuelIssued: string;
  fuelConsumed: string | null;
  fuelClosing: string | null;
  fuelNorm: string | null;
  fuelDeviation: string | null;
  fuelDeviationPct: string | null;
  normBreakdown: {
    lines: Array<{
      key: string;
      rate: number;
      quantity: number;
      unit: string;
      litres: number;
    }>;
  } | null;
  notes: string | null;
  cancelReason: string | null;
  conditionOnIssue: string | null;
  conditionIssueNotes: string | null;
  conditionOnReturn: string | null;
  conditionReturnNotes: string | null;
  vehicleId: number;
  vehicle: {
    garageNumber: string;
    plateNumber: string | null;
    meterType: string;
    fuelTypeId: number | null;
  } | null;
  driverId: number;
  driver: { lastName: string; firstName: string; personnelNumber: string } | null;
  coDriverId: number | null;
  coDriver: { lastName: string; firstName: string; personnelNumber: string } | null;
  tasks: Array<{
    id: number;
    sequence: number;
    fromPoint: string | null;
    flightNumber: string | null;
    aircraftReg: string | null;
    standNumber: string | null;
    toPoint: string | null;
    distanceKm: string | null;
    engineHours: string | null;
    transportRequest: { id: number; number: string } | null;
  }>;
  fuelIssues: Array<{
    id: number;
    documentNumber: string;
    issuedAt: string;
    volume: string;
    tank: { code: string } | null;
    fuelType: { id: number; code: string; name: string } | null;
  }>;
}

/** Статусы, в которых по листу можно внести заправку: выдан и ещё не закрыт. */
const REFUEL_STATUSES: string[] = [
  WaybillStatus.ISSUED,
  WaybillStatus.IN_PROGRESS,
  WaybillStatus.SUBMITTED,
];

type ActionKind = 'issue' | 'submit' | 'close' | 'cancel' | 'refuel' | null;

/**
 * Варианты состояния техники — один список для выдачи и для закрытия.
 *
 * Список строится на уровне модуля, вне компонента, поэтому t() здесь
 * недоступен: перевод применяется в месте отрисовки. Хранится русский
 * текст из общего пакета — он же ключ перевода.
 */
const CONDITION_OPTIONS = Object.values(VehicleCondition).map((value) => ({
  value,
  labelKey: CONDITION_LABEL[value],
}));

export function WaybillDrawer({ waybillId, onClose }: Props) {
  const { t } = useTranslation();

  const { can, user } = useAuth();
  const dictionaries = useDictionaries();
  const taskLayout = user?.activeOffice.taskLayout ?? 'FLIGHT';
  const isAddressLayout = taskLayout === 'ADDRESS';
  const isRegionDistrictLayout = taskLayout === 'REGION_DISTRICT';
  const hasLocationList = user?.activeOffice.taskAddressALocations ?? false;
  const download = useDownload();
  const [action, setAction] = useState<ActionKind>(null);
  const [editOpen, setEditOpen] = useState(false);
  // Состояние допуска приходит из карточки: от него зависит, спрашивать ли
  // причину обхода и показывать ли предупреждение о недопуске.
  const [clearance, setClearance] = useState<{
    state: string;
    allowed: boolean;
    overridable: boolean;
  } | null>(null);
  // Заключение механика — второй допуск, от которого зависит выпуск.
  const [technical, setTechnical] = useState<{
    state: string;
    allowed: boolean;
    overridable: boolean;
  } | null>(null);
  const [form] = Form.useForm();
  // Следим за выбором, чтобы предупредить об акте до нажатия кнопки.
  const returnCondition = Form.useWatch('conditionOnReturn', form) as
    VehicleCondition | undefined;

  const open = waybillId !== null;

  const waybill = useQuery({
    queryKey: ['waybill', waybillId],
    enabled: open,
    queryFn: async () => (await api.get<WaybillDetail>(`/waybills/${waybillId}`)).data,
  });

  const invalidate = [
    ['waybill'],
    ['waybills'],
    ['office-summary'],
    ['vehicles'],
    ['fuel-tanks'],
  ];

  const w = waybill.data;
  const status = w?.status;
  const meterType = w?.vehicle?.meterType;

  /*
   * Приём и закрытие: показание счётчика на возврат вводится либо само,
   * либо пробегом (наработкой) за смену — «водитель говорит: проехал 40 км».
   * В запрос уходит одно поле нужного счётчика, служебные поля формы
   * (режим ввода) — нет: сервер отклоняет лишние свойства.
   */
  const toPayload = (
    kind: Exclude<ActionKind, null>,
    values: Record<string, unknown>,
  ): Record<string, unknown> => {
    if (kind !== 'submit' && kind !== 'close') return values;
    const { meterMode, meterValue, refuels, ...rest } = values;
    const payload: Record<string, unknown> = { ...rest };
    if (meterValue !== undefined && meterValue !== null) {
      const delta = meterMode === 'delta';
      if (meterType === 'ODOMETER') payload[delta ? 'distanceKm' : 'odometerEnd'] = meterValue;
      if (meterType === 'ENGINE_HOURS') {
        payload[delta ? 'engineHoursWorked' : 'engineHoursEnd'] = meterValue;
      }
    }
    const filled = ((refuels as Array<{ fuelTypeId?: number; volume?: number }>) ?? []).filter(
      (row) => row?.fuelTypeId && row?.volume,
    );
    if (filled.length > 0) payload.refuels = filled;
    return payload;
  };

  const run = useApiMutation(
    async ({
      kind,
      values,
    }: {
      kind: Exclude<ActionKind, null>;
      values: Record<string, unknown>;
    }) =>
      (
        await api.post(
          `/waybills/${waybillId}/${kind === 'refuel' ? 'refuels' : kind}`,
          toPayload(kind, values),
        )
      ).data,
    {
      successMessage: t('Операция выполнена'),
      invalidate: [...invalidate, ['fuel-issues']],
    },
  );

  // «Выехал» — без формы: отметка момента выезда одним нажатием.
  const depart = useApiMutation(
    async () => (await api.post(`/waybills/${waybillId}/depart`)).data,
    { successMessage: t('Выезд отмечен'), invalidate },
  );

  const openAction = (kind: Exclude<ActionKind, null>): void => {
    form.resetFields();
    if (kind === 'submit' || kind === 'close') {
      const end = meterType === 'ENGINE_HOURS' ? w?.engineHoursEnd : w?.odometerEnd;
      form.setFieldsValue({
        meterMode: 'reading',
        meterValue: end ? Number(end) : undefined,
        refuels: [],
        // По умолчанию — как выдали: изменение состояния должно быть
        // осознанным действием, а не следствием незаполненного поля.
        conditionOnReturn:
          (w?.conditionOnIssue as VehicleCondition) ?? VehicleCondition.SERVICEABLE,
      });
    }
    if (kind === 'refuel') {
      form.setFieldsValue({ fuelTypeId: w?.vehicle?.fuelTypeId ?? undefined });
    }
    if (kind === 'issue') {
      setClearance(null);
      setTechnical(null);
      form.setFieldsValue({ conditionOnIssue: VehicleCondition.SERVICEABLE });
    }
    setAction(kind);
  };

  const deviationPct =
    w?.fuelDeviationPct === null || w?.fuelDeviationPct === undefined
      ? null
      : Number(w.fuelDeviationPct);

  const fuelTypeOptions = (dictionaries.data?.fuelTypes ?? []).map((fuel) => ({
    value: fuel.id,
    label: fuel.name,
  }));

  /*
   * Показание на возврат — одним полем того счётчика, по которому ведётся
   * учёт машины. Режим «пробег за смену»: диспетчер вводит, сколько
   * проехал водитель, а показание считается само (и наоборот, в режиме
   * показания подсказка пишет пробег).
   */
  const isOdometer = meterType === 'ODOMETER';
  const startReading = isOdometer ? w?.odometerStart : w?.engineHoursStart;
  const meterUnit = isOdometer ? 'км' : 'мч';
  const meterBlock =
    meterType === 'ODOMETER' || meterType === 'ENGINE_HOURS' ? (
      <>
        <Form.Item name="meterMode" label={isOdometer ? t('Пробег вводится') : t('Наработка вводится')}>
          <Segmented
            options={[
              {
                label: isOdometer ? t('Показанием одометра') : t('Показанием моточасов'),
                value: 'reading',
              },
              {
                label: isOdometer ? t('Пробегом за смену') : t('Наработкой за смену'),
                value: 'delta',
              },
            ]}
          />
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const delta = getFieldValue('meterMode') === 'delta';
            const value = getFieldValue('meterValue') as number | null | undefined;
            const start = startReading === null || startReading === undefined ? null : Number(startReading);
            const hint =
              value === null || value === undefined || start === null
                ? start !== null
                  ? `${t('На выезд')}: ${fmt(start, 1)} ${meterUnit}`
                  : undefined
                : delta
                  ? `${t('Показание на возврат будет')}: ${fmt(start + value, 1)} ${meterUnit}`
                  : `${t('За смену')}: ${fmt(value - start, 1)} ${meterUnit}`;
            return (
              <Form.Item
                name="meterValue"
                label={
                  delta
                    ? isOdometer
                      ? t('Пробег за смену')
                      : t('Наработка за смену')
                    : isOdometer
                      ? t('Одометр на возврат')
                      : t('Моточасы на возврат')
                }
                extra={hint}
                rules={[
                  {
                    validator: (_rule, v: number | null | undefined) =>
                      !delta && v !== null && v !== undefined && start !== null && v < start
                        ? Promise.reject(new Error(t('Меньше показания на выезд')))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber min={0} addonAfter={meterUnit} style={{ width: '100%' }} />
              </Form.Item>
            );
          }}
        </Form.Item>
      </>
    ) : null;

  /** Заправки за смену — литры и марка, сколько угодно строк. */
  const refuelsBlock = (
    <Form.List name="refuels">
      {(fields, { add, remove }) => (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('Заправка за смену')}
          </Typography.Text>
          {fields.map((field) => (
            <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
              <Form.Item
                name={[field.name, 'volume']}
                rules={[{ required: true, message: t('Литры') }]}
              >
                <InputNumber min={0.01} placeholder={t('Литры')} addonAfter="л" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item
                name={[field.name, 'fuelTypeId']}
                rules={[{ required: true, message: t('Марка') }]}
              >
                <Select placeholder={t('Марка топлива')} options={fuelTypeOptions} style={{ width: 200 }} />
              </Form.Item>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
            </Space>
          ))}
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => add({ fuelTypeId: w?.vehicle?.fuelTypeId ?? undefined })}
          >
            {t('Добавить заправку')}
          </Button>
        </div>
      )}
    </Form.List>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={880}
        loading={waybill.isLoading}
        title={
          w ? (
            <Space>
              <span>{w.number}</span>
              <Tag color={WAYBILL_STATUS_COLOR[w.status]}>
                {t(WAYBILL_STATUS_LABEL[w.status] ?? w.status)}
              </Tag>
            </Space>
          ) : (
            'Путевой лист'
          )
        }
        extra={
          w && (
            <Space>
              {/* Идентификатор перед кнопками: действия должны оставаться
                у самого края, куда тянется рука. */}
              <EntityId id={w.id} />
              {/* Редактировать можно только черновик: после выдачи лист
                фиксирует показания и задания, а не остаётся черновиком. */}
              {status === WaybillStatus.DRAFT && can(PERMISSIONS.WAYBILL_UPDATE) && (
                <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                  {t('Изменить')}
                </Button>
              )}
              {can(PERMISSIONS.WAYBILL_PRINT) && (
                <>
                  {/* Печать — только после выдачи: черновик ещё редактируется
                    и не фиксирует то, что попало бы в документ (см. ту же
                    проверку на сервере в printData()). Выгрузка в Excel —
                    просто данные, а не бланк, ей это ограничение не нужно. */}
                  {status !== WaybillStatus.DRAFT && (
                    <Button
                      icon={<PrinterOutlined />}
                      onClick={() =>
                        window.open(`/waybills/${w.id}/print`, '_blank', 'noopener')
                      }
                    >
                      {t('Печать')}
                    </Button>
                  )}
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={() =>
                      void download(
                        `/waybills/${w.id}/export.csv`,
                        {},
                        `waybill-${w.number}.csv`,
                      )
                    }
                  >
                    Excel
                  </Button>
                </>
              )}
              {status === WaybillStatus.DRAFT && can(PERMISSIONS.WAYBILL_ISSUE) && (
                <Button type="primary" onClick={() => openAction('issue')}>
                  {t('Выдать водителю')}
                </Button>
              )}
              {status === WaybillStatus.ISSUED && can(PERMISSIONS.WAYBILL_UPDATE) && (
                <Button loading={depart.isPending} onClick={() => depart.mutate(undefined)}>
                  {t('Выехал')}
                </Button>
              )}
              {(status === WaybillStatus.ISSUED ||
                status === WaybillStatus.IN_PROGRESS) &&
                can(PERMISSIONS.WAYBILL_UPDATE) && (
                  <Button type="primary" onClick={() => openAction('submit')}>
                    {t('Принять от водителя')}
                  </Button>
                )}
              {status === WaybillStatus.SUBMITTED && can(PERMISSIONS.WAYBILL_CLOSE) && (
                <Button type="primary" onClick={() => openAction('close')}>
                  {t('Закрыть с расчётом')}
                </Button>
              )}
              {status !== WaybillStatus.CLOSED &&
                status !== WaybillStatus.CANCELLED &&
                can(PERMISSIONS.WAYBILL_CANCEL) && (
                  <Button danger onClick={() => openAction('cancel')}>
                    {t('Аннулировать')}
                  </Button>
                )}
            </Space>
          )
        }
      >
        {w && (
          <>
            {w.status === WaybillStatus.CANCELLED && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="Путевой лист аннулирован"
                description={w.cancelReason}
              />
            )}

            {w.status === WaybillStatus.CLOSED && (
              <Space size="large" style={{ marginBottom: 16 }}>
                <Statistic title={t('Норма, л')} value={fmt(w.fuelNorm, 2)} />
                <Statistic title={t('Факт, л')} value={fmt(w.fuelConsumed, 2)} />
                <Statistic
                  title={t('Отклонение')}
                  value={
                    deviationPct === null
                      ? '—'
                      : `${deviationPct > 0 ? '+' : ''}${deviationPct} %`
                  }
                  valueStyle={{ color: deviationColor(deviationPct) }}
                />
              </Space>
            )}

            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label={t('Техника')}>
                {w.vehicle?.garageNumber ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Водитель')}>
                {w.driver ? `${w.driver.lastName} ${w.driver.firstName}` : '—'}
              </Descriptions.Item>
              {w.coDriver && (
                <Descriptions.Item label={t('Второй водитель')} span={2}>
                  {`${w.coDriver.lastName} ${w.coDriver.firstName}`}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('Начало')}>
                {dayjs(w.validFrom).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('Окончание')}>
                {dayjs(w.validTo).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
              {w.departedAt && (
                <Descriptions.Item label={t('Выехал')} span={2}>
                  {dayjs(w.departedAt).format('DD.MM.YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {/* Один счётчик на технику — показываем только его. */}
              {meterType === 'ODOMETER' && (
                <>
                  <Descriptions.Item label={t('Одометр')}>
                    {fmt(w.odometerStart)} → {fmt(w.odometerEnd)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Пробег за смену')}>
                    {fmt(w.distanceKm, 1)} км
                  </Descriptions.Item>
                </>
              )}
              {meterType === 'ENGINE_HOURS' && (
                <>
                  <Descriptions.Item label={t('Моточасы')}>
                    {fmt(w.engineHoursStart)} → {fmt(w.engineHoursEnd)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('Наработка')}>
                    {fmt(w.engineHours, 1)} мч
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={t('Топливо на начало')}>
                {fmt(w.fuelOpening, 2)} л
              </Descriptions.Item>
              <Descriptions.Item label={t('Заправка')}>
                {fmt(w.fuelIssued, 2)} л
              </Descriptions.Item>
              <Descriptions.Item label={t('Остаток на конец')}>
                {fmt(w.fuelClosing, 2)} л
              </Descriptions.Item>
              <Descriptions.Item label={t('Примечание')}>
                {w.notes ?? '—'}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 24 }}>Задания</h4>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={w.tasks}
              columns={[
                { title: '№', dataIndex: 'sequence', width: 50 },
                ...(isRegionDistrictLayout
                  ? [
                      { title: t('Регион'), dataIndex: 'fromPoint', width: 160 },
                      { title: t('Район'), dataIndex: 'aircraftReg', width: 160 },
                      { title: t('Адрес Б'), dataIndex: 'toPoint' },
                    ]
                  : isAddressLayout
                    ? [
                        { title: t('Адрес А'), dataIndex: 'fromPoint', width: 160 },
                        ...(hasLocationList
                          ? [
                              {
                                title: t('Локация'),
                                dataIndex: 'aircraftReg',
                                width: 140,
                              },
                            ]
                          : []),
                        { title: t('Адрес Б'), dataIndex: 'toPoint' },
                      ]
                    : [
                        { title: t('Рейс'), dataIndex: 'flightNumber', width: 90 },
                        { title: t('Борт'), dataIndex: 'aircraftReg', width: 100 },
                        { title: t('Стоянка'), dataIndex: 'standNumber', width: 90 },
                        { title: t('Куда'), dataIndex: 'toPoint' },
                      ]),
                {
                  title: t('км'),
                  dataIndex: 'distanceKm',
                  align: 'right',
                  width: 80,
                  render: (v: string | null) => fmt(v, 1),
                },
                {
                  title: t('мч'),
                  dataIndex: 'engineHours',
                  align: 'right',
                  width: 80,
                  render: (v: string | null) => fmt(v, 1),
                },
                {
                  title: t('Заявка'),
                  width: 150,
                  render: (_: unknown, task: (typeof w.tasks)[number]) =>
                    task.transportRequest ? (
                      <Tag bordered={false} color="gold">
                        {task.transportRequest.number}
                      </Tag>
                    ) : (
                      '—'
                    ),
                },
              ]}
            />

            <Space style={{ marginTop: 24, marginBottom: 8, justifyContent: 'space-between', width: '100%' }}>
              <h4 style={{ margin: 0 }}>{t('Заправка')}</h4>
              {status &&
                REFUEL_STATUSES.includes(status) &&
                can(PERMISSIONS.WAYBILL_UPDATE) && (
                  <Button size="small" icon={<PlusOutlined />} onClick={() => openAction('refuel')}>
                    {t('Добавить заправку')}
                  </Button>
                )}
            </Space>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={w.fuelIssues}
              locale={{ emptyText: t('Заправок не было') }}
              columns={[
                { title: t('Документ'), dataIndex: 'documentNumber' },
                {
                  title: t('Дата'),
                  dataIndex: 'issuedAt',
                  render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
                },
                {
                  title: t('Марка'),
                  render: (_: unknown, row: WaybillDetail['fuelIssues'][number]) =>
                    row.fuelType?.name ?? '—',
                },
                {
                  title: t('Ёмкость'),
                  render: (_: unknown, row: WaybillDetail['fuelIssues'][number]) =>
                    row.tank?.code ?? t('АЗС'),
                },
                {
                  title: t('Объём, л'),
                  dataIndex: 'volume',
                  align: 'right',
                  render: (v: string) => fmt(v, 2),
                },
              ]}
            />

            {w.normBreakdown?.lines && (
              <>
                <h4 style={{ marginTop: 24 }}>Расшифровка расчёта нормы</h4>
                <Typography.Paragraph type="secondary">
                  {t(
                    'Сохранена на момент закрытия. Изменение норм задним числом её не затронет.',
                  )}
                </Typography.Paragraph>
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={w.normBreakdown.lines}
                  columns={[
                    { title: t('Составляющая'), dataIndex: 'key' },
                    { title: t('Ставка'), dataIndex: 'rate', align: 'right' },
                    {
                      title: t('Объём'),
                      align: 'right',
                      render: (_: unknown, row: { quantity: number; unit: string }) =>
                        `${row.quantity} ${row.unit}`,
                    },
                    { title: t('Литров'), dataIndex: 'litres', align: 'right' },
                  ]}
                />
              </>
            )}

            {/* Журнал внизу, а не вкладкой: карточка путевого листа читается
              сверху вниз одним потоком, и разрывать её вкладками ради
              служебного раздела значит спрятать сам документ. */}
            <h4 style={{ marginTop: 24 }}>{t('Журнал действий')}</h4>
            <EntityAuditLog entity="Waybill" entityId={w.id} limit={30} />
          </>
        )}

        <Modal
          open={action !== null}
          title={
            action === 'issue'
              ? 'Выдача путевого листа'
              : action === 'submit'
                ? 'Приём от водителя'
                : action === 'close'
                  ? 'Закрытие с расчётом'
                  : action === 'refuel'
                    ? t('Заправка')
                    : 'Аннулирование'
          }
          okText={t('Подтвердить')}
          cancelText={t('Отмена')}
          confirmLoading={run.isPending}
          onCancel={() => setAction(null)}
          onOk={() => {
            void form.validateFields().then((values) => {
              run.mutate(
                { kind: action as Exclude<ActionKind, null>, values },
                { onSuccess: () => setAction(null) },
              );
            });
          }}
        >
          <Form form={form} layout="vertical">
            {action === 'issue' && (
              <>
                {/* Медицинский допуск не вводится, а показывается: его источник —
                  заключение врача из здравпункта. Галочки «медосмотр пройден»
                  здесь больше нет, потому что подтверждать собственную
                  проверку заинтересованной стороне нельзя. */}
                <MedicalClearanceCard driverId={w?.driverId} onState={setClearance} />
                {/* Заключение механика — так же показывается, а не вводится:
                  исправность техники подтверждает механик, а не диспетчер. */}
                <TechnicalClearanceCard vehicleId={w?.vehicleId} onState={setTechnical} />

                <Typography.Paragraph type="secondary">
                  {t(
                    'Система проверит удостоверение, необходимый допуск, медицинское заключение и заключение механика.',
                  )}
                </Typography.Paragraph>

                {technical && !technical.allowed && technical.overridable && (
                  <Form.Item
                    name="technicalOverrideReason"
                    label={t('Причина выпуска без заключения механика')}
                    tooltip={t(
                      'Доступно только с правом waybill.override_technical. Причина сохраняется в путевом листе и в журнале действий.',
                    )}
                    rules={[{ required: true, message: t('Укажите причину') }]}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder={t(
                        'Например: механик на выезде, осмотр внесён с бумаги',
                      )}
                    />
                  </Form.Item>
                )}

                {technical?.state === 'FAILED' && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('Выпуск невозможен')}
                    description={t(
                      'Механик не выпустил технику на линию. Это решение не обходится никакими правами — нужна замена машины.',
                    )}
                  />
                )}

                {/* Состояние на выдаче — точка отсчёта. Без него при возврате
                  не с чем сравнивать, и любая поломка выглядит как «было
                  и до меня». */}
                <Form.Item
                  name="conditionOnIssue"
                  label={t('Состояние техники при выдаче')}
                  rules={[{ required: true, message: t('Обязательное поле') }]}
                >
                  <Select
                    options={CONDITION_OPTIONS.map((option) => ({
                      value: option.value,
                      label: t(option.labelKey),
                    }))}
                  />
                </Form.Item>
                <Form.Item name="conditionIssueNotes" label={t('Замечания при выдаче')}>
                  <Input.TextArea rows={2} maxLength={600} />
                </Form.Item>

                {/* Обход медосмотра — отдельное поле и отдельное право.
                  Общая галочка «вопреки замечаниям» его не снимает. */}
                {clearance && !clearance.allowed && clearance.overridable && (
                  <Form.Item
                    name="medicalOverrideReason"
                    label={t('Причина выдачи без медосмотра')}
                    tooltip={t(
                      'Доступно только с правом waybill.override_medical. Причина сохраняется в путевом листе и в журнале действий.',
                    )}
                    rules={[{ required: true, message: t('Укажите причину') }]}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder={t(
                        'Например: здравпункт закрыт, осмотр внесён с бумаги',
                      )}
                    />
                  </Form.Item>
                )}

                {clearance && clearance.state === 'FAILED' && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('Выдача невозможна')}
                    description={t(
                      'Врач не допустил водителя к работе. Это решение не обходится никакими правами — нужна замена водителя.',
                    )}
                  />
                )}

                <Form.Item name="overrideEligibility" valuePropName="checked">
                  <Checkbox>
                    {t(
                      'Выдать вопреки замечаниям по документам (действие попадёт в журнал аудита)',
                    )}
                  </Checkbox>
                </Form.Item>
              </>
            )}

            {action === 'refuel' && (
              <>
                <Form.Item
                  name="volume"
                  label={t('Литры')}
                  rules={[{ required: true, message: t('Укажите объём') }]}
                >
                  <InputNumber min={0.01} step={1} addonAfter="л" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  name="fuelTypeId"
                  label={t('Марка топлива')}
                  rules={[{ required: true, message: t('Выберите марку') }]}
                >
                  <Select options={fuelTypeOptions} />
                </Form.Item>
                <Form.Item name="notes" label={t('Примечание')}>
                  <Input placeholder={t('Например: АЗС на трассе, чек №…')} />
                </Form.Item>
              </>
            )}

            {action === 'submit' && (
              <>
                {meterBlock}
                {refuelsBlock}
              </>
            )}

            {action === 'close' && (
              <>
                {meterBlock}
                {refuelsBlock}
                <Form.Item
                  name="fuelClosing"
                  label={t('Замеренный остаток в баке, л')}
                  tooltip={t(
                    'Если не указать, фактический расход будет принят равным нормативному — и перерасход не проявится',
                  )}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="conditionOnReturn"
                  label={t('Состояние техники при возврате')}
                  tooltip={
                    w?.conditionOnIssue
                      ? `${t('При выдаче было')}: ${t(CONDITION_LABEL[w.conditionOnIssue as VehicleCondition] ?? w.conditionOnIssue)}`
                      : undefined
                  }
                  rules={[{ required: true, message: t('Обязательное поле') }]}
                >
                  <Select
                    options={CONDITION_OPTIONS.map((option) => ({
                      value: option.value,
                      label: t(option.labelKey),
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="conditionReturnNotes"
                  label={t('Описание повреждений')}
                  tooltip={t(
                    'Текст попадёт в акт — пишите так, как объясните это через месяц',
                  )}
                >
                  <Input.TextArea rows={3} maxLength={600} />
                </Form.Item>

                {/* Предупреждение до нажатия, а не сюрприз после: акт —
                  документ, на который ссылаются при удержании. */}
                {returnCondition &&
                  needsConditionAct(
                    (w?.conditionOnIssue as VehicleCondition | null) ?? null,
                    returnCondition,
                  ) && (
                    <Alert
                      type="warning"
                      showIcon
                      message={t('Будет составлен акт о состоянии техники')}
                      description={
                        w?.driver
                          ? `${t('В акте будет указан водитель, принявший технику')}: ${w.driver.lastName} ${w.driver.firstName}`
                          : undefined
                      }
                    />
                  )}
              </>
            )}

            {action === 'cancel' && (
              <Form.Item name="reason" label={t('Причина')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
            )}
          </Form>
        </Modal>
      </Drawer>
      <WaybillFormModal
        open={editOpen}
        editing={w ?? null}
        onClose={() => setEditOpen(false)}
      />
    </>
  );
}
